import { computed, ref } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import type {
  AddRestrictionsCheckInOutSettingInput,
  AddRestrictionsPlacingVariantsInput,
  AddRestrictionsServiceGroupInput,
  GetCheckInOutRestrictionsQuery,
  GetPlacingVariantsRestrictionsQuery,
  GetServiceGroupsRestrictionsQuery,
  RemoveRestrictionsCheckInOutSettingInput,
  RemoveRestrictionsPlacingVariantInput,
  RemoveRestrictionsServiceGroupInput,
  UpdateRestrictionsCheckInOutSettingInput,
  UpdateRestrictionsPlacingVariantInput,
  UpdateRestrictionsServiceGroupInput,
} from '@/graphql/generated';
import {
  useAddCheckInOutRestrictionMutation,
  useAddPlacingVariantsRestrictionMutation,
  useAddServiceGroupsRestrictionMutation,
  useGetCheckInOutRestrictionsQuery,
  useGetPlacingVariantsRestrictionsQuery,
  useGetServiceGroupsRestrictionsQuery,
  useRemoveCheckInOutRestrictionMutation,
  useRemovePlacingVariantsRestrictionMutation,
  useRemoveServiceGroupsRestrictionMutation,
  useUpdateCheckInOutRestrictionMutation,
  useUpdatePlacingVariantsRestrictionMutation,
  useUpdateServiceGroupsRestrictionMutation,
} from '@/graphql/generated';

export type CheckInOutRestrictionsType = GetCheckInOutRestrictionsQuery['restrictionsCheckInOutSettings'];
export type CheckInOutRestrictionsTableRestrictionType = ArrayElementType<
  GetCheckInOutRestrictionsQuery['restrictionsCheckInOutSettings']['table']
>['restriction'];

export type PlacingVariantsRestrictionsType = GetPlacingVariantsRestrictionsQuery['restrictionsPlacingVariants'];
export type PlacingVariantsRestrictionsTableItemType = ArrayElementType<
  GetPlacingVariantsRestrictionsQuery['restrictionsPlacingVariants']['table']
>;
export type PlacingVariantsRestrictionsCellDataType = {
  roomCategory: string;
  placingVariant: string;
  restrictionId?: number | null;
};

export type ServiceGroupsRestrictionsType = GetServiceGroupsRestrictionsQuery['restrictionsServiceGroups'];
export type ServiceGroupsRestrictionsTableItemType = ArrayElementType<
  GetServiceGroupsRestrictionsQuery['restrictionsServiceGroups']['table']
>;
export type ServiceGroupsRestrictionsCellDataType = {
  serviceGroup: string;
  restrictionId?: number | null;
};

const checkInOutRestrictions = ref<CheckInOutRestrictionsType>();
const placingVariantsRestrictions = ref<PlacingVariantsRestrictionsType>();
const serviceGroupsRestrictions = ref<ServiceGroupsRestrictionsType>();

const isGetCheckInOutRestrictionsLoading = ref(false);
const isGetPlacingVariantsRestrictionsLoading = ref(false);
const isGetServiceGroupsRestrictionsLoading = ref(false);

export const useRestrictionsStore = () => {
  //#region Расчётные часы
  const checkInOutRestrictionsChessPeriod = computed(() => {
    return {
      from: checkInOutRestrictions.value?.grid[0]?.period.from,
      to: checkInOutRestrictions.value?.grid.at(-1)?.period.to,
    };
  });

  const getCheckInOutRestrictions = async (tariffScheduleCid: string) => {
    isGetCheckInOutRestrictionsLoading.value = true;

    const { request } = useGetCheckInOutRestrictionsQuery({
      tariffScheduleCid,
    });
    const result = await request();

    isGetCheckInOutRestrictionsLoading.value = false;

    if (!result.errors && result.data) {
      setCheckInOutRestrictions(result.data.restrictionsCheckInOutSettings);
    }
  };

  const setCheckInOutRestrictions = (data: CheckInOutRestrictionsType) => {
    checkInOutRestrictions.value = data;
  };

  const addCheckInOutRestriction = async (payload: AddRestrictionsCheckInOutSettingInput) => {
    const { request } = useAddCheckInOutRestrictionMutation({
      input: payload,
    });
    const result = await request();

    if (!result.errors && result.data?.addRestrictionsCheckInOutSetting.Grid) {
      setCheckInOutRestrictions(result.data.addRestrictionsCheckInOutSetting.Grid);
    }

    return result;
  };

  const updateCheckInOutRestriction = async (payload: UpdateRestrictionsCheckInOutSettingInput) => {
    const { request } = useUpdateCheckInOutRestrictionMutation({
      input: payload,
    });
    const result = await request();

    if (!result.errors && result.data?.updateRestrictionsCheckInOutSetting.Grid) {
      setCheckInOutRestrictions(result.data.updateRestrictionsCheckInOutSetting.Grid);
    }

    return result;
  };

  const removeCheckInOutRestriction = async (payload: RemoveRestrictionsCheckInOutSettingInput) => {
    const { request } = useRemoveCheckInOutRestrictionMutation({
      input: payload,
    });
    const result = await request();
    if (!result.errors && result.data?.removeRestrictionsCheckInOutSetting.Grid) {
      setCheckInOutRestrictions(result.data.removeRestrictionsCheckInOutSetting.Grid);
    }

    return result;
  };
  //#endregion Расчётные часы

  //#region Варианты размещения
  const placingVariantsRestrictionsChessPeriod = computed(() => {
    return placingVariantsRestrictions.value?.gridPeriod;
  });

  const getPlacingVariantsRestrictions = async (tariffScheduleCid: string) => {
    isGetPlacingVariantsRestrictionsLoading.value = true;

    const { request } = useGetPlacingVariantsRestrictionsQuery({
      tariffScheduleCid,
    });
    const result = await request();

    isGetPlacingVariantsRestrictionsLoading.value = false;

    if (!result.errors && result.data) {
      setPlacingVariantsRestrictions(result.data.restrictionsPlacingVariants);
    }
  };

  const setPlacingVariantsRestrictions = (data: PlacingVariantsRestrictionsType) => {
    placingVariantsRestrictions.value = data;
  };

  const addPlacingVariantsRestriction = async (payload: AddRestrictionsPlacingVariantsInput) => {
    const { request } = useAddPlacingVariantsRestrictionMutation({
      input: payload,
    });
    const result = await request();
    if (!result.errors && result.data?.addRestrictionsPlacingVariants.Grid) {
      setPlacingVariantsRestrictions(result.data.addRestrictionsPlacingVariants.Grid);
    }

    return result;
  };

  const updatePlacingVariantsRestriction = async (payload: UpdateRestrictionsPlacingVariantInput) => {
    const { request } = useUpdatePlacingVariantsRestrictionMutation({
      input: payload,
    });
    const result = await request();

    if (!result.errors && result.data?.updateRestrictionsPlacingVariant.Grid) {
      setPlacingVariantsRestrictions(result.data.updateRestrictionsPlacingVariant.Grid);
    }

    return result;
  };

  const removePlacingVariantsRestriction = async (payload: RemoveRestrictionsPlacingVariantInput) => {
    const { request } = useRemovePlacingVariantsRestrictionMutation({
      input: payload,
    });
    const result = await request();
    if (!result.errors && result.data?.removeRestrictionsPlacingVariant.Grid) {
      setPlacingVariantsRestrictions(result.data.removeRestrictionsPlacingVariant.Grid);
    }

    return result;
  };
  //#endregion Варианты размещения

  //#region Группы услуг
  const serviceGroupsRestrictionsChessPeriod = computed(() => {
    return serviceGroupsRestrictions.value?.gridPeriod;
  });

  const getServiceGroupsRestrictions = async (tariffScheduleCid: string) => {
    isGetServiceGroupsRestrictionsLoading.value = true;

    const { request } = useGetServiceGroupsRestrictionsQuery({
      tariffScheduleCid,
    });
    const result = await request();

    isGetServiceGroupsRestrictionsLoading.value = false;

    if (!result.errors && result.data) {
      setServiceGroupsRestrictions(result.data.restrictionsServiceGroups);
    }
  };

  const setServiceGroupsRestrictions = (data: ServiceGroupsRestrictionsType) => {
    serviceGroupsRestrictions.value = data;
  };

  const addServiceGroupsRestriction = async (payload: AddRestrictionsServiceGroupInput) => {
    const { request } = useAddServiceGroupsRestrictionMutation({
      input: payload,
    });
    const result = await request();

    if (!result.errors && result.data?.addRestrictionsServiceGroup.Grid) {
      setServiceGroupsRestrictions(result.data.addRestrictionsServiceGroup.Grid);
    }

    return result;
  };

  const updateServiceGroupsRestriction = async (payload: UpdateRestrictionsServiceGroupInput) => {
    const { request } = useUpdateServiceGroupsRestrictionMutation({
      input: payload,
    });
    const result = await request();

    if (!result.errors && result.data?.updateRestrictionsServiceGroup.Grid) {
      setServiceGroupsRestrictions(result.data.updateRestrictionsServiceGroup.Grid);
    }

    return result;
  };

  const removeServiceGroupsRestriction = async (payload: RemoveRestrictionsServiceGroupInput) => {
    const { request } = useRemoveServiceGroupsRestrictionMutation({
      input: payload,
    });
    const result = await request();
    if (!result.errors && result.data?.removeRestrictionsServiceGroup.Grid) {
      setServiceGroupsRestrictions(result.data.removeRestrictionsServiceGroup.Grid);
    }

    return result;
  };
  //#endregion Группы услуг

  return {
    checkInOutRestrictions,
    checkInOutRestrictionsChessPeriod,
    isGetCheckInOutRestrictionsLoading,
    getCheckInOutRestrictions,
    addCheckInOutRestriction,
    updateCheckInOutRestriction,
    removeCheckInOutRestriction,

    placingVariantsRestrictions,
    placingVariantsRestrictionsChessPeriod,
    isGetPlacingVariantsRestrictionsLoading,
    getPlacingVariantsRestrictions,
    addPlacingVariantsRestriction,
    updatePlacingVariantsRestriction,
    removePlacingVariantsRestriction,

    serviceGroupsRestrictions,
    serviceGroupsRestrictionsChessPeriod,
    isGetServiceGroupsRestrictionsLoading,
    getServiceGroupsRestrictions,
    addServiceGroupsRestriction,
    updateServiceGroupsRestriction,
    removeServiceGroupsRestriction,
  };
};
